import { twMerge } from 'tailwind-merge'

// 유틸리티 함수 — tailwind-merge로 충돌하는 클래스를 올바르게 병합
export function cn(...classes: (string | undefined | null | false)[]): string {
  return twMerge(classes.filter(Boolean).join(' '))
}

/**
 * 서버 응답의 ISO datetime 문자열을 Date 객체로 안전하게 파싱한다.
 * 백엔드는 DB 에 UTC naive 로 저장하므로 응답에 타임존 suffix 가 빠진 채로
 * 나가는 경우가 있다 (예: "2026-05-19T03:00:00"). JS Date 는 이를 로컬
 * 시간으로 해석해 KST 환경에서 정확히 9시간 어긋난다.
 *
 * → suffix(`Z`, `+HH:MM`, `-HH:MM`) 가 없으면 명시적으로 `Z` 를 붙여 UTC 로 해석.
 */
export function parseServerDate(dateString: string): Date {
  const hasTz =
    dateString.endsWith('Z') ||
    dateString.includes('+') ||
    dateString.includes('-', 10)
  return hasTz ? new Date(dateString) : new Date(dateString + 'Z')
}

/**
 * UTC datetime 문자열을 KST로 변환하여 한국어 형식으로 표시
 * @param dateString - ISO 8601 datetime 문자열 (타임존 정보 없으면 UTC로 간주)
 * @returns 한국어 날짜/시간 문자열 (예: "2024년 11월 28일 오후 9:00")
 */
export function formatDateKST(dateString: string): string {
  return parseServerDate(dateString).toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * UTC datetime 문자열을 KST로 변환하여 시간만 표시
 * @param dateString - ISO 8601 datetime 문자열
 * @returns 한국어 시간 문자열 (예: "오후 9:00:30")
 */
export function formatTimeKST(dateString: string): string {
  return parseServerDate(dateString).toLocaleTimeString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * UTC datetime 문자열을 "N분/시간/일 전" 상대 표현으로 포맷 (KST 기준).
 * 24시간 이내는 분/시간, 30일 이내는 일, 그 이상은 날짜로 fallback 한다.
 * (MetaApprovalQueueSection 의 로컬 formatRelative 를 공용 헬퍼로 승격)
 * @param dateString - ISO 8601 datetime 문자열 (타임존 정보 없으면 UTC로 간주)
 * @returns "3시간 전" 같은 한국어 상대 표현 (파싱 실패 시 빈 문자열)
 */
export function formatRelativeKST(dateString: string): string {
  const date = parseServerDate(dateString)
  if (Number.isNaN(date.getTime())) return ''

  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)

  if (diffMin < 1) return '방금'
  if (diffMin < 60) return `${diffMin}분 전`
  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `${diffHour}시간 전`
  const diffDay = Math.floor(diffHour / 24)
  if (diffDay < 30) return `${diffDay}일 전`
  return date.toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })
}

// 두 시간 사이의 소요 시간을 한국어 형식으로 계산
export function calculateDuration(
  startedAt: string | null,
  completedAt: string | null,
): string | null {
  if (!startedAt || !completedAt) return null
  const start = new Date(startedAt)
  const end = new Date(completedAt)
  const durationMs = end.getTime() - start.getTime()
  const minutes = Math.floor(durationMs / (1000 * 60))
  const seconds = Math.floor((durationMs % (1000 * 60)) / 1000)
  if (minutes > 0) return `${minutes}분 ${seconds}초`
  return `${seconds}초`
}
